import { getContext } from '@/context';

const STORAGE_KEY = 'genaiStudioGeneration';
const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

type PersistedGeneration = {
  generationId: string;
  placeholderUuid: string;
  presetTitle: string;
  startedAt: number;
  presetType?: 'image' | 'video';
};

const isValidShape = (data: unknown): data is PersistedGeneration =>
  typeof data === 'object' &&
  data !== null &&
  typeof (data as PersistedGeneration).generationId === 'string' &&
  typeof (data as PersistedGeneration).placeholderUuid === 'string' &&
  typeof (data as PersistedGeneration).presetTitle === 'string' &&
  typeof (data as PersistedGeneration).startedAt === 'number';

const readAll = (): PersistedGeneration[] => {
  const { internals } = getContext();
  const state = internals.getState({ permanent: true });
  const data = state[STORAGE_KEY];

  // Backward compat: old single-object format
  if (data && !Array.isArray(data)) {
    return isValidShape(data) ? [data] : [];
  }

  if (!Array.isArray(data)) return [];
  return data.filter(isValidShape);
};

const saveGeneration = async (data: PersistedGeneration): Promise<void> => {
  try {
    const { internals } = getContext();
    const current = internals.getState({ permanent: true });
    const existing = readAll();
    await internals.setState(
      { ...current, [STORAGE_KEY]: [...existing, data] },
      { permanent: true }
    );
  } catch {
    // eslint-disable-next-line no-console
    console.log('[GenerationStorage] Failed to save generation state');
  }
};

const loadGenerations = (): PersistedGeneration[] => {
  try {
    const all = readAll();
    return all.filter((item) => Date.now() - item.startedAt <= MAX_AGE_MS);
  } catch {
    return [];
  }
};

const clearGeneration = async (generationId: string): Promise<void> => {
  try {
    const { internals } = getContext();
    const current = internals.getState({ permanent: true });
    const existing = readAll();
    const filtered = existing.filter((g) => g.generationId !== generationId);
    await internals.setState(
      { ...current, [STORAGE_KEY]: filtered.length > 0 ? filtered : null },
      { permanent: true }
    );
  } catch {
    // eslint-disable-next-line no-console
    console.log('[GenerationStorage] Failed to clear generation state');
  }
};

export { saveGeneration, loadGenerations, clearGeneration };
export type { PersistedGeneration };
